import { Injectable } from '@angular/core';
import { CacheService, CacheStoragesEnum } from 'ng2-cache-service';

import { plainToClass } from 'class-transformer';
import { Observable } from 'rxjs/Observable';

import 'rxjs/add/operator/retryWhen';
import 'rxjs/add/operator/scan';
import 'rxjs/add/operator/delay';

import { ObservableRequestService } from 'app/services/observable-request.service';
import { ConstantsService } from 'app/services/constants-service';

import { ApplicationType, ApplicationValue } from './application-type.model';
import { User } from 'app/shared/models/user.model';

/**
 * Service to cache Application Type values in browser localstorage.
 */
@Injectable()
export class WarmUpService {

    constructor(private cacheService: CacheService,
        private observableRequest: ObservableRequestService) {
    }

    fetchAndLoadAllApplicationTypes() {
        this.observableRequest
            .getRequestWithMapResponse(ConstantsService.ALL_APPLICATION_TYPES_URL)
            .map(response => {
                return plainToClass(ApplicationType, response.json().arrResult as Object[]);
            })
            .retryWhen(e => e.scan<number>((errorCount, err) => {
                if (errorCount >= ConstantsService.WARM_UP_SERVICE_RETRIES) {
                    throw err;
                }
                return errorCount + 1;
            }, 0).delay(1000))
            .subscribe(applicationTypes => {
                this.setApplicationTypesInLS(applicationTypes);
            });
    }

    setApplicationTypesInLS(applicationTypes: ApplicationType[]) {
        // setting the expiry time for the localstorage values as 24hours.
        for (let i = 0; i < applicationTypes.length; i++) {
            this.setApplicationTypeInLS(applicationTypes[i]);
        }
    }

    setApplicationTypeInLS(applicationType: ApplicationType) {
        this.cacheService.set(applicationType.name, applicationType.values,
            { expires: ConstantsService.LOCAL_STORAGE_TIMEOUT });
    }

    getApplicationValue(applicationTypeName: string): any {
        const applicationValues = this.cacheService.get(applicationTypeName);
        if (!applicationValues) {
            return this.fetchApplicationType(applicationTypeName);
        }
        return applicationValues;
    }

    fetchApplicationType(applicationTypeName: string) {
        return this.observableRequest
            .getRequestWithMapResponse(ConstantsService.APPLICATION_TYPE_URL + applicationTypeName)
            .map(response => {
                return plainToClass(ApplicationType, response.json().arrResult as Object);
            });
    }


    fetchAndLoadAllUsers(): any {
        this.observableRequest
            .getRequest(ConstantsService.USERS)
            .map(response => {
                return plainToClass(User, response.arrResult as Object[]);
            })
            .retryWhen(e => e.scan<number>((errorCount, err) => {
                if (errorCount >= ConstantsService.WARM_UP_SERVICE_RETRIES) {
                    throw err;
                }
                return errorCount + 1;
            }, 0).delay(1000))
            .subscribe(userList => {
                this.setUsersListInLS(userList);
                return userList;
            });
    }

    getUsersList(): User[] {
        const users = this.cacheService.get('users');
        if (!users) {
            return this.fetchAndLoadAllUsers();
        } else {
            return users;
        }
    }

    checkLSHasUsers(): boolean {
        return this.cacheService.get('users');
    }

    refreshUserList() {
        this.cacheService.remove('users');
        this.getUsersList();
    }

    setUsersListInLS(usersList: User[]) {
        this.cacheService.set('users', usersList, { expires: ConstantsService.LOCAL_STORAGE_TIMEOUT });
    }

  excludeCustomPeriod<T extends { name: string }>(list: T[]): T[] {
    return list.filter(item => item.name !== 'Custom');
  }

    getApplicationValueList(applicationTypeName: string, setValue: any) {
        const list = this.getApplicationValue(applicationTypeName);
        if (list instanceof Observable) {
          return list.subscribe(
            response => {
              this.setApplicationTypeInLS(response);
              setValue(response.values);
            }
          );
        } else {
         setValue(list);
        }
    }
}

export class ApplicationTypeConstants {
    public static ADDRESS_TYPE = 'AddressType';
    public static APPLICATION_TYPE = 'ApplicationTypes';
    public static AUTHORIZED_TYPE = 'AuthorizedType';
    public static CASH_FLOW = 'CashFlow';
    public static COMMUNICATION_TYPE = 'CommunicationType';
    public static CONNECTION_MODE = 'ConnectionMode';
    public static COUNTRY_TYPE = 'CountryType';
    public static CURRENCY = 'Currency';
    public static DOCUMENT_TYPE = 'DocumentType';
    public static GENDER_TYPE = 'GenderType';
    public static LANGUAGE = 'Language';
    public static LEGAL_STRUCTURE = 'LegalStructure';
    public static APPLICATION_FUNCTIONALITY = 'PlatformFunctionality';
    public static ORG_ACTIVITY_CATEGORY = 'OrgActivityCategory';
    public static ORG_ACTIVITY_SUB_CATEGORY = 'OrgActivitySubCategory';
    public static SURVEY_QUESTION_TYPE = 'QuestionType';
    public static STOCK_EXCHANGE = 'StockExchange';
    public static SURVEY_LOCATION = 'SurveyLocation';
    public static TIME_KEEPING = 'TimekeepingActivity';
    public static TRANSACTION_STATUS = 'TransactionStatus';
    public static TRANSACTION_FREQUENCY_PERIOD = 'TransactionFrequencyPeriod';
    public static RELATION_ROLE_TYPES = 'RelationRole';
    public static OPPOSITE_RELATION_ROLE_TYPES = 'OppositeRelationRole';
    public static SANCTION_MATCH_RESOLUTION = 'SanctionMatchResolution';
    public static PANAMA_PAPERS_MATCH_RESOLUTION = 'PanamaPapersMatchResolution';
    public static FALSE_MATCH_REASONS = 'SanctionFalseMatchReason';
    public static ENTITY_RISK_CHANCE = 'RiskChance';
    public static ENTITY_RISK_TYPE = 'RiskType';
    public static ENTITY_RISK_IMPACT = 'RiskImpact';
    public static CLIENT_STATE_TYPE = 'ClientStateType';
}



// WEBPACK FOOTER //
// ./src/app/services/warmup/warmup.service.ts